import { useState, useEffect } from 'react';
import { getAdminSuppliers, createSupplier, updateSupplier, deleteSupplier } from '@/lib/api';
import { Plus, Edit2, Trash2, X, Check, Truck } from 'lucide-react';

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', delivery_delay_weeks: 3 });

  const fetch = () => { getAdminSuppliers().then(r => setSuppliers(r.data)); };
  useEffect(() => { fetch(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    await createSupplier(form);
    setForm({ name: '', email: '', delivery_delay_weeks: 3 });
    setShowAdd(false);
    fetch();
  };

  const handleUpdate = async (id) => {
    await updateSupplier(id, form);
    setEditing(null);
    fetch();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    await deleteSupplier(id);
    fetch();
  };

  return (
    <div data-testid="admin-suppliers" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold text-[#111] tracking-tight">Fournisseurs</h1>
        <button onClick={() => { setShowAdd(true); setForm({ name: '', email: '', delivery_delay_weeks: 3 }); }} data-testid="add-supplier-button" className="flex items-center gap-2 px-4 py-2 bg-[#00A5FF] text-white text-sm font-semibold rounded-sm hover:bg-[#0090e0] transition-colors duration-200">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {showAdd && (
        <div className="border border-[#00A5FF] bg-[#00A5FF]/5 p-6 mb-6">
          <h3 className="text-sm font-bold mb-3">Nouveau fournisseur</h3>
          <form onSubmit={handleAdd} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-black/50 block mb-1">Nom *</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="px-3 py-2 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none" />
            </div>
            <div>
              <label className="text-xs text-black/50 block mb-1">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="px-3 py-2 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none" />
            </div>
            <div>
              <label className="text-xs text-black/50 block mb-1">Délai (semaines)</label>
              <input type="number" value={form.delivery_delay_weeks} onChange={(e) => setForm({...form, delivery_delay_weeks: parseInt(e.target.value)})} className="w-20 px-3 py-2 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none" />
            </div>
            <button type="submit" className="px-4 py-2 bg-[#00A5FF] text-white text-sm font-semibold rounded-sm">Ajouter</button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-black/50 hover:text-black">Annuler</button>
          </form>
        </div>
      )}

      <div className="border border-black/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50 border-b border-black/10">
              <th className="text-left px-4 py-2 font-semibold">Nom</th>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-center px-4 py-2 font-semibold">Délai livraison</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} className="border-t border-black/5 hover:bg-[#F8F9FA] transition-colors duration-200">
                {editing === s.id ? (
                  <>
                    <td className="px-4 py-2"><input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="px-2 py-1 text-sm border border-black/15 w-full" /></td>
                    <td className="px-4 py-2"><input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="px-2 py-1 text-sm border border-black/15 w-full" /></td>
                    <td className="px-4 py-2 text-center"><input type="number" value={form.delivery_delay_weeks} onChange={(e) => setForm({...form, delivery_delay_weeks: parseInt(e.target.value)})} className="w-16 px-2 py-1 text-sm border border-black/15 text-center" /></td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleUpdate(s.id)} className="p-1 text-green-600 hover:bg-green-50 mr-1"><Check size={14} /></button>
                      <button onClick={() => setEditing(null)} className="p-1 text-black/30"><X size={14} /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 font-medium flex items-center gap-2"><Truck size={14} className="text-[#00A5FF]" /> {s.name}</td>
                    <td className="px-4 py-2 text-black/60">{s.email || '-'}</td>
                    <td className="px-4 py-2 text-center">{s.delivery_delay_weeks} sem.</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => { setEditing(s.id); setForm({ name: s.name, email: s.email, delivery_delay_weeks: s.delivery_delay_weeks }); }} className="p-1 text-[#00A5FF] hover:bg-[#00A5FF]/10 mr-1"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(s.id)} className="p-1 text-black/20 hover:text-[#E8231B]"><Trash2 size={14} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
